import { StateCreator } from 'zustand';
import { setLocaleCookie } from '@/utils/locale-cookie';
import { normalizeSectionType } from '@/utils/section-type';

/** Persistable section shape (structural match for SectionData from studio) */
export type StudioSectionData = {
  id: string;
  type: string;
  name: string;
  properties: Record<string, Record<string, unknown>>;
};

export type StudioViewportSize = 'mobile' | 'tablet' | 'desktop';
export type StudioPageType = 'rich-text-editor' | 'section-based';
export type StudioComponentProperties = Record<string, Record<string, unknown>>;
/**
 * Recommended payment method key used by the storefront cart.
 *
 * Concrete values come from the runtime `webshop-list-payment-methods`
 * system config (typically `CARD` / `CASH` / `PROFORMA`) and may be
 * extended server-side, so the type is intentionally a `string` rather
 * than a closed literal union. The reserved sentinel `'NONE'` means
 * "no recommendation": the storefront should not preselect any payment
 * method and not render the "Recommended" badge.
 */
export type StudioRecommendedPaymentMethod = string;
export const STUDIO_NO_RECOMMENDED_PAYMENT_METHOD = 'NONE';

export type StudioGeneralSettings = {
  faviconSrc: string;
  theme: string;
  supportedLanguages: Array<{ code: string; label: string }>;
  defaultLanguageCode: string;
  currency: string;
  recommendedPaymentMethod: StudioRecommendedPaymentMethod;
  orderReviewPromptDays: number;
  features: {
    productDetailReviews: boolean;
    ageVerification: boolean;
    globalSearch: boolean;
    priceForGuests: boolean;
    cartForGuests: boolean;
    imageChangeOnHover: boolean;
    favorites: boolean;
    priceFilter: boolean;
    freeShippingLabel: boolean;
  };
  scripts: Array<{
    name: string;
    script: string;
    locale?: string; // empty means apply to all locales
    loadTiming?: 'afterInteractive' | 'beforeInteractive';
  }>;
};

export type StudioPageComponentData = {
  type: string;
  variant: string;
  properties: StudioComponentProperties;
};

export type StudioPageLayout = {
  showHeader: boolean;
  showFooter: boolean;
};

export type StudioPageData = {
  id: string;
  name: string;
  url: string;
  pageType: StudioPageType;
  richTextContent: Record<string, string>;
  richTextContentUrl?: string;
  sections: StudioSectionData[];
  components: Record<string, StudioPageComponentData>;
  layout: StudioPageLayout;
};

export type StaticContentMeta = { id: string; version: number };

type LocalStudio = {
  activeProjectId: string | null;
  /** False until `loadProjectContent` has run for `activeProjectId` at least once. */
  isProjectContentLoaded: boolean;
  pages: StudioPageData[];
  selectedPageId: string | null;
  selectedSectionId: string | null;
  selectedComponentType: string | null;
  selectedSettingsKey: string | null;
  viewportSize: StudioViewportSize;
  previewLocale: string;
  generalSettings: StudioGeneralSettings;
  staticContentMeta: Record<string, StaticContentMeta>;
  sidebarOpen: boolean;
};

export type StudioSlice = {
  studio: LocalStudio;
  toggleSidebar: () => void;
  setActiveProjectId: (id: string | null) => void;
  setStaticContentMeta: (key: string, meta: StaticContentMeta) => void;
  removeStaticContentMeta: (key: string) => void;
  bumpContentVersion: (key: string) => void;
  loadProjectContent: (data: {
    pages: StudioPageData[];
    components: Record<string, StudioPageComponentData>;
    generalSettings: StudioGeneralSettings;
    meta: Record<string, StaticContentMeta>;
  }) => void;
  createPage: (
    page: Omit<StudioPageData, 'sections' | 'components' | 'richTextContent' | 'layout'> & {
      richTextContent?: Record<string, string>;
      sections?: StudioSectionData[];
      components?: Record<string, StudioPageComponentData>;
      layout?: StudioPageLayout;
    },
  ) => void;
  updatePage: (
    pageId: string,
    updates: Partial<Omit<StudioPageData, 'id' | 'sections' | 'components' | 'richTextContent'>> & {
      richTextContent?: Record<string, string>;
      sections?: StudioSectionData[];
      components?: Record<string, StudioPageComponentData>;
    },
  ) => void;
  deletePage: (pageId: string) => void;
  selectPage: (pageId: string | null) => void;
  setSelectedComponentType: (componentType: string | null) => void;
  setSelectedSettingsKey: (settingsKey: string | null) => void;
  updateGeneralSettings: (updates: Partial<StudioGeneralSettings>) => void;
  upsertSelectedPageComponent: (component: StudioPageComponentData) => void;
  setSections: (sections: StudioSectionData[]) => void;
  setSelectedSectionId: (id: string | null) => void;
  setViewportSize: (size: StudioViewportSize) => void;
  setPreviewLocale: (locale: string) => void;
  addSection: (section: StudioSectionData) => void;
  reorderSections: (orderedIds: string[]) => void;
  deleteSection: (sectionId: string) => void;
  updateSectionProperties: (
    sectionId: string,
    properties: Record<string, Record<string, unknown>>,
  ) => void;
};

const defaultGeneralSettings: StudioGeneralSettings = {
  faviconSrc:
    'https://storage.googleapis.com/e3-webshop-image-storage/e3-webshop/assets/2025/10/09/logo.png',
  theme: 'bndk',
  supportedLanguages: [
    {
      code: 'ro',
      label: 'Romanian',
    },
  ],
  defaultLanguageCode: 'ro',
  currency: 'RON',
  recommendedPaymentMethod: 'CARD',
  orderReviewPromptDays: 7,
  features: {
    productDetailReviews: false,
    ageVerification: true,
    globalSearch: true,
    priceForGuests: false,
    cartForGuests: false,
    imageChangeOnHover: true,
    favorites: true,
    priceFilter: true,
    freeShippingLabel: true,
  },
  scripts: [],
};

const defaultStudio: LocalStudio = {
  activeProjectId: null,
  isProjectContentLoaded: false,
  pages: [],
  selectedPageId: null,
  selectedSectionId: null,
  selectedComponentType: null,
  selectedSettingsKey: null,
  viewportSize: 'desktop',
  previewLocale: 'ro',
  generalSettings: defaultGeneralSettings,
  staticContentMeta: {},
  sidebarOpen: true,
};

const normalizeRichTextContent = (value: unknown): Record<string, string> => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, string>;
  }
  if (typeof value === 'string' && value.trim()) {
    return { _legacy: value };
  }
  return {};
};

const defaultPageLayout: StudioPageLayout = {
  showHeader: true,
  showFooter: true,
};

const normalizePageLayout = (value: unknown): StudioPageLayout => {
  if (!value || typeof value !== 'object') {
    return defaultPageLayout;
  }

  const layout = value as Partial<StudioPageLayout>;
  return {
    showHeader: typeof layout.showHeader === 'boolean' ? layout.showHeader : true,
    showFooter: typeof layout.showFooter === 'boolean' ? layout.showFooter : true,
  };
};

const normalizePage = (page: Partial<StudioPageData>): StudioPageData => ({
  id: String(page.id ?? ''),
  name: String(page.name ?? ''),
  url: String(page.url ?? ''),
  pageType: (page.pageType as StudioPageType) ?? 'section-based',
  richTextContent: normalizeRichTextContent(page.richTextContent),
  richTextContentUrl:
    typeof page.richTextContentUrl === 'string' ? page.richTextContentUrl : undefined,
  sections: Array.isArray(page.sections)
    ? page.sections.map(section => ({
        ...section,
        type: normalizeSectionType(section.type),
      }))
    : [],
  components:
    page.components && typeof page.components === 'object'
      ? (page.components as Record<string, StudioPageComponentData>)
      : {},
  layout: normalizePageLayout(page.layout),
});

const getPages = (studio: Partial<LocalStudio>): StudioPageData[] =>
  Array.isArray(studio.pages)
    ? studio.pages.map(page => normalizePage(page as StudioPageData))
    : [];

const getSelectedPageId = (studio: Partial<LocalStudio>): string | null =>
  typeof studio.selectedPageId === 'string' ? studio.selectedPageId : null;

const normalizeStudio = (studio: Partial<LocalStudio>): LocalStudio => ({
  activeProjectId:
    typeof studio.activeProjectId === 'string'
      ? studio.activeProjectId
      : defaultStudio.activeProjectId,
  isProjectContentLoaded:
    typeof studio.isProjectContentLoaded === 'boolean'
      ? studio.isProjectContentLoaded
      : defaultStudio.isProjectContentLoaded,
  pages: getPages(studio),
  selectedPageId: getSelectedPageId(studio),
  selectedSectionId:
    typeof studio.selectedSectionId === 'string' || studio.selectedSectionId === null
      ? studio.selectedSectionId
      : defaultStudio.selectedSectionId,
  selectedComponentType:
    typeof studio.selectedComponentType === 'string' || studio.selectedComponentType === null
      ? studio.selectedComponentType
      : defaultStudio.selectedComponentType,
  selectedSettingsKey:
    typeof studio.selectedSettingsKey === 'string' || studio.selectedSettingsKey === null
      ? studio.selectedSettingsKey
      : defaultStudio.selectedSettingsKey,
  viewportSize:
    studio.viewportSize === 'mobile' ||
    studio.viewportSize === 'tablet' ||
    studio.viewportSize === 'desktop'
      ? studio.viewportSize
      : defaultStudio.viewportSize,
  generalSettings:
    studio.generalSettings && typeof studio.generalSettings === 'object'
      ? {
          ...defaultGeneralSettings,
          ...(studio.generalSettings as Partial<StudioGeneralSettings>),
          features: {
            ...defaultGeneralSettings.features,
            ...((studio.generalSettings as Partial<StudioGeneralSettings>).features ?? {}),
          },
          supportedLanguages: Array.isArray(
            (studio.generalSettings as Partial<StudioGeneralSettings>).supportedLanguages,
          )
            ? ((studio.generalSettings as Partial<StudioGeneralSettings>)
                .supportedLanguages as StudioGeneralSettings['supportedLanguages'])
            : defaultGeneralSettings.supportedLanguages,
        }
      : defaultGeneralSettings,
  previewLocale:
    typeof studio.previewLocale === 'string' ? studio.previewLocale : defaultStudio.previewLocale,
  staticContentMeta:
    studio.staticContentMeta && typeof studio.staticContentMeta === 'object'
      ? (studio.staticContentMeta as Record<string, StaticContentMeta>)
      : defaultStudio.staticContentMeta,
  sidebarOpen:
    typeof studio.sidebarOpen === 'boolean' ? studio.sidebarOpen : defaultStudio.sidebarOpen,
});

const toStudioState = (
  studio: Partial<LocalStudio>,
  overrides: Partial<LocalStudio> = {},
): LocalStudio => {
  const normalized = normalizeStudio(studio);
  return {
    ...normalized,
    ...overrides,
  };
};

export const createStudioSlice: StateCreator<StudioSlice> = set => ({
  studio: defaultStudio,
  toggleSidebar: () =>
    set(state => ({
      studio: { ...state.studio, sidebarOpen: !state.studio.sidebarOpen },
    })),
  setActiveProjectId: activeProjectId =>
    set(state => ({
      studio: {
        ...defaultStudio,
        activeProjectId,
        viewportSize: state.studio.viewportSize,
      },
    })),
  setStaticContentMeta: (key, meta) =>
    set(state => ({
      studio: {
        ...state.studio,
        staticContentMeta: { ...state.studio.staticContentMeta, [key]: meta },
      },
    })),
  removeStaticContentMeta: key =>
    set(state => {
      const staticContentMeta = { ...state.studio.staticContentMeta };
      delete staticContentMeta[key];
      return { studio: { ...state.studio, staticContentMeta } };
    }),
  bumpContentVersion: key =>
    set(state => {
      const existing = state.studio.staticContentMeta[key];
      if (!existing) return state;
      return {
        studio: {
          ...state.studio,
          staticContentMeta: {
            ...state.studio.staticContentMeta,
            [key]: { ...existing, version: existing.version + 1 },
          },
        },
      };
    }),
  loadProjectContent: ({ pages, components, generalSettings, meta }) =>
    set(state => {
      const defaultLocale =
        generalSettings?.defaultLanguageCode ??
        generalSettings?.supportedLanguages?.[0]?.code ??
        'ro';
      const normalizedPages = pages.map(page => normalizePage(page));
      const pagesWithComponents = normalizedPages.map(page => {
        const rtc = { ...page.richTextContent };
        if (rtc._legacy !== undefined) {
          rtc[defaultLocale] = rtc._legacy;
          delete rtc._legacy;
        }
        return {
          ...page,
          richTextContent: rtc,
          components: { ...page.components, ...components },
        };
      });
      return {
        studio: {
          ...state.studio,
          isProjectContentLoaded: true,
          pages: pagesWithComponents,
          generalSettings: {
            ...defaultGeneralSettings,
            ...generalSettings,
            features: { ...defaultGeneralSettings.features, ...(generalSettings?.features ?? {}) },
            supportedLanguages: Array.isArray(generalSettings?.supportedLanguages)
              ? generalSettings.supportedLanguages
              : defaultGeneralSettings.supportedLanguages,
            scripts: Array.isArray(generalSettings?.scripts)
              ? generalSettings.scripts.map(s => ({
                  name: s.name ?? '',
                  script: s.script ?? '',
                  locale: s.locale,
                  loadTiming:
                    s.loadTiming === 'beforeInteractive'
                      ? ('beforeInteractive' as const)
                      : ('afterInteractive' as const),
                }))
              : defaultGeneralSettings.scripts,
          },
          staticContentMeta: meta,
          previewLocale:
            generalSettings?.defaultLanguageCode ??
            generalSettings?.supportedLanguages?.[0]?.code ??
            state.studio.previewLocale,
          selectedPageId: pagesWithComponents.find(page => page.id)?.id ?? null,
          selectedSectionId: null,
          selectedComponentType: null,
          selectedSettingsKey: null,
        },
      };
    }),
  createPage: page =>
    set(state => {
      const pages = getPages(state.studio);
      const sharedComponents = pages[0]?.components ?? {};
      const newPage: StudioPageData = {
        id: page.id,
        name: page.name,
        url: page.url,
        pageType: page.pageType,
        richTextContent: page.richTextContent ?? {},
        sections: page.sections ?? [],
        components: { ...sharedComponents, ...(page.components ?? {}) },
        layout: normalizePageLayout(page.layout),
      };
      const nextPages = [...pages, newPage];
      return {
        studio: {
          ...toStudioState(state.studio, { pages: nextPages, selectedPageId: newPage.id }),
          selectedSectionId: null,
          selectedComponentType: null,
          selectedSettingsKey: null,
        },
      };
    }),
  updatePage: (pageId, updates) =>
    set(state => {
      const pages = getPages(state.studio);
      const selectedPageId = getSelectedPageId(state.studio);
      const nextPages = pages.map(page =>
        page.id === pageId
          ? {
              ...page,
              ...updates,
              sections: updates.sections ?? page.sections,
              components: updates.components ?? page.components,
            }
          : page,
      );
      return {
        studio: toStudioState(state.studio, { pages: nextPages, selectedPageId }),
      };
    }),
  deletePage: pageId =>
    set(state => {
      const pages = getPages(state.studio);
      const selectedPageId = getSelectedPageId(state.studio);
      const nextPages = pages.filter(page => page.id !== pageId);
      const nextSelectedPageId =
        selectedPageId === pageId
          ? (nextPages[0]?.id ?? null)
          : selectedPageId && nextPages.some(page => page.id === selectedPageId)
            ? selectedPageId
            : (nextPages[0]?.id ?? null);

      return {
        studio: {
          ...toStudioState(state.studio, {
            pages: nextPages,
            selectedPageId: nextSelectedPageId,
          }),
          selectedSectionId: null,
          selectedComponentType: null,
          selectedSettingsKey: null,
        },
      };
    }),
  selectPage: selectedPageId =>
    set(state => {
      if (!selectedPageId) {
        return state;
      }

      const pages = getPages(state.studio);
      return {
        studio: {
          ...toStudioState(state.studio, { pages, selectedPageId }),
          selectedSectionId: null,
          selectedComponentType: null,
          selectedSettingsKey: null,
        },
      };
    }),
  setSelectedComponentType: selectedComponentType =>
    set(state => ({
      studio: {
        ...toStudioState(state.studio, { selectedComponentType, selectedSettingsKey: null }),
        selectedSectionId: null,
      },
    })),
  setSelectedSettingsKey: selectedSettingsKey =>
    set(state => ({
      studio: {
        ...toStudioState(state.studio, {
          selectedSettingsKey,
          selectedPageId: null,
          selectedComponentType: null,
          selectedSectionId: null,
        }),
      },
    })),
  updateGeneralSettings: updates =>
    set(state => ({
      studio: toStudioState(state.studio, {
        generalSettings: {
          ...state.studio.generalSettings,
          ...updates,
          features: {
            ...state.studio.generalSettings.features,
            ...(updates.features ?? {}),
          },
          supportedLanguages:
            updates.supportedLanguages ?? state.studio.generalSettings.supportedLanguages,
        },
      }),
    })),
  upsertSelectedPageComponent: component =>
    set(state => {
      const pages = getPages(state.studio);
      const selectedPageId = getSelectedPageId(state.studio);
      if (!selectedPageId) {
        return {
          studio: toStudioState(state.studio, { pages, selectedPageId }),
        };
      }

      const nextPages = pages.map(page =>
        page.id === selectedPageId
          ? {
              ...page,
              components: {
                ...page.components,
                [component.type]: component,
              },
            }
          : page,
      );

      return {
        studio: toStudioState(state.studio, { pages: nextPages, selectedPageId }),
      };
    }),
  setSections: sections =>
    set(state => {
      const pages = getPages(state.studio);
      const selectedPageId = getSelectedPageId(state.studio);
      if (!selectedPageId) {
        return {
          studio: toStudioState(state.studio, { pages, selectedPageId, selectedSectionId: null }),
        };
      }

      const nextPages = pages.map(page =>
        page.id === selectedPageId ? { ...page, sections } : page,
      );
      return {
        studio: toStudioState(state.studio, {
          pages: nextPages,
          selectedPageId,
          selectedSectionId: null,
          selectedComponentType: null,
        }),
      };
    }),
  setSelectedSectionId: selectedSectionId =>
    set(state => ({
      studio: toStudioState(state.studio, { selectedSectionId, selectedComponentType: null }),
    })),
  setViewportSize: viewportSize =>
    set(state => ({
      studio: toStudioState(state.studio, { viewportSize }),
    })),
  setPreviewLocale: previewLocale => {
    setLocaleCookie(previewLocale);
    set(state => ({
      studio: toStudioState(state.studio, { previewLocale }),
    }));
  },
  addSection: section =>
    set(state => {
      const pages = getPages(state.studio);
      const selectedPageId = getSelectedPageId(state.studio);

      if (!selectedPageId) {
        return {
          studio: toStudioState(state.studio, { pages, selectedPageId }),
        };
      }

      const nextPages = pages.map(page =>
        page.id === selectedPageId ? { ...page, sections: [...page.sections, section] } : page,
      );

      return {
        studio: toStudioState(state.studio, { pages: nextPages, selectedPageId }),
      };
    }),
  reorderSections: orderedIds =>
    set(state => {
      const pages = getPages(state.studio);
      const selectedPageId = getSelectedPageId(state.studio);
      const currentSections = pages.find(page => page.id === selectedPageId)?.sections ?? [];
      const sections = orderedIds
        .map(id => currentSections.find(s => s.id === id))
        .filter((s): s is StudioSectionData => s !== undefined);
      const nextPages = pages.map(page =>
        page.id === selectedPageId ? { ...page, sections } : page,
      );

      return {
        studio: toStudioState(state.studio, { pages: nextPages, selectedPageId }),
      };
    }),
  deleteSection: sectionId =>
    set(state => {
      const pages = getPages(state.studio);
      const selectedPageId = getSelectedPageId(state.studio);
      const nextPages = pages.map(page =>
        page.id === selectedPageId
          ? { ...page, sections: page.sections.filter(section => section.id !== sectionId) }
          : page,
      );

      return {
        studio: {
          ...toStudioState(state.studio, { pages: nextPages, selectedPageId }),
          selectedSectionId:
            state.studio.selectedSectionId === sectionId ? null : state.studio.selectedSectionId,
        },
      };
    }),
  updateSectionProperties: (sectionId, properties) =>
    set(state => {
      const pages = getPages(state.studio);
      const selectedPageId = getSelectedPageId(state.studio);
      const nextPages = pages.map(page =>
        page.id === selectedPageId
          ? {
              ...page,
              sections: page.sections.map(section =>
                section.id === sectionId ? { ...section, properties } : section,
              ),
            }
          : page,
      );

      return {
        studio: toStudioState(state.studio, { pages: nextPages, selectedPageId }),
      };
    }),
});
